/**
 * TemplatesTour-specific types and step definitions.
 *
 * The TemplatesTour walks users through message templates — selecting a
 * template from the picker, seeing it populate the message input, then
 * navigating to Settings to learn how to create custom templates.
 */

import type { TourStep } from '../types';

/** Unique identifiers for each TemplatesTour step */
export type TemplatesTourStepId =
  | 'templates_welcome'
  | 'highlight_template_btn'
  | 'highlight_template_item'
  | 'highlight_message_input'
  | 'settings_nav'
  | 'settings_templates_tab'
  | 'highlight_templates_management'
  | 'highlight_add_template_btn'
  | 'highlight_variables_panel'
  | 'highlight_add_template_form'
  | 'templates_complete';

/** Ordered list of all TemplatesTour steps */
export const TEMPLATES_TOUR_STEPS: TourStep[] = [
  {
    id: 'templates_welcome',
    type: 'modal',
    title: '',
    content: '',
  },
  {
    id: 'highlight_template_btn',
    type: 'highlight',
    targetSelector: '[data-tour-id="template-btn"]',
    title: 'Message Templates',
    content:
      'Click the templates icon to open your message templates. These are pre-written messages you can use to respond quickly and consistently.',
    placement: 'top',
    advanceOnElement: '[data-tour-id="template-picker"]',
  },
  {
    id: 'highlight_template_item',
    type: 'highlight',
    targetSelector: '[data-tour-id="template-picker"]',
    title: 'Select a Template',
    content:
      'Choose a template to insert it into your message. Try selecting "Relationship Builder / General Touchpoint" to see how it works.',
    placement: 'top',
    allowInteraction: true,
    advanceOnElementRemoved: '[data-tour-id="template-picker"]',
  },
  {
    id: 'highlight_message_input',
    type: 'highlight',
    targetSelector: '[data-tour-id="message-input"]',
    title: 'Template Applied',
    content:
      'The template has been inserted into your message input with variables automatically filled in from the contact\'s information. You can edit the message before sending, or send it as-is.',
    placement: 'top',
  },
  {
    id: 'settings_nav',
    type: 'highlight',
    targetSelector: '[data-tour-id="settings"]',
    title: 'Settings',
    content: 'Now let\'s see where you can create your own templates. Click here to go to Settings.',
    placement: 'right',
    advanceOnNavigate: '/settings',
  },
  {
    id: 'settings_templates_tab',
    type: 'highlight',
    targetSelector: '[data-tour-id="settings-nav-templates"]',
    title: 'Message Templates',
    content: 'Click here to open the Message Templates settings where you can create and manage your agency\'s templates.',
    placement: 'right',
    advanceOnElement: '[data-tour-id="templates-management"]',
  },
  {
    id: 'highlight_templates_management',
    type: 'highlight',
    targetSelector: '[data-tour-id="templates-management"]',
    title: 'Template Management',
    content:
      'This is your template headquarters. Here you can create, edit, and delete message templates that your entire agency can access. Every team member will see these templates when composing messages.',
    placement: 'left',
    positionInside: true,
  },
  {
    id: 'highlight_add_template_btn',
    type: 'highlight',
    targetSelector: '[data-tour-id="add-template-btn"]',
    title: 'Add a Template',
    content: 'Click here to create a new custom template for your agency.',
    placement: 'bottom',
    advanceOnElement: '[data-tour-id="add-template-form"]',
  },
  {
    id: 'highlight_variables_panel',
    type: 'highlight',
    targetSelector: '[data-tour-id="available-variables-btn"]',
    title: 'Available Variables',
    content:
      'Click this to see all available variables you can use in your templates. Variables like {FirstName}, {AgentName}, and {AgencyName} are automatically replaced with real data when the template is used in a conversation.',
    placement: 'bottom',
    advanceOnElement: '[data-tour-id="variables-panel"]',
  },
  {
    id: 'highlight_add_template_form',
    type: 'highlight',
    targetSelector: '[data-tour-id="add-template-form"]',
    title: 'Create Your Template',
    content:
      'Give your template a name, then write the message content. Use variables in curly brackets like {FirstName} to personalize it. When you\'re done, hit "Add Template" to save it for your whole agency.',
    placement: 'top',
    allowInteraction: true,
    advanceOnElementRemoved: '[data-tour-id="add-template-form"]',
  },
  {
    id: 'templates_complete',
    type: 'modal',
    title: 'Templates Tour Complete',
    content:
      'You now know how to use message templates to save time and maintain consistency across your agency.',
  },
];
